import {Injectable} from '@angular/core'
import {HttpClient, HttpParams} from '@angular/common/http'

import {throwError as observableThrowError, Observable} from 'rxjs'
import {tap} from 'rxjs/operators'

import {
  Contract,
  ContractType,
  ContractEntry,
  Registration,
  RegistrationEntry,
} from '@app/core/services/rest-api.model'
import {
  ApiSearchRequest,
  ApiSearchResponse,
} from '@app/core/services/api.interface'
import {LoggerService} from '@app/core/services/logger.service'
import {ContractAction} from './contract-action.interface'
import {Note, Notes} from '../terminal/terminal.service'

export {Contract, ContractType, ContractEntry, Registration, RegistrationEntry}

export interface GetContractsRequest extends ApiSearchRequest {
  domain: string
  exact?: boolean
}
export interface GetContractsResponse extends ApiSearchResponse<Contract> {}
export interface GetContractEntriesRequest extends ApiSearchRequest {
  domain: string
  id: number
}

export interface UpdateContractEntryRequest {
  id: number
  data: any
  status?: string
  unlink?: boolean
}

export interface GetContractEntriesResponse extends ApiSearchResponse<ContractEntry> {}
export interface GetRegistrationsRequest extends ApiSearchRequest {
  domain: string
  entries?: boolean
}
export interface GetRegistrationsResponse extends ApiSearchResponse<Registration> {}

export interface CreateRegistrationRequest {
  name: string
  contractIds: number[]
  numberOfEntries: number
}

export interface ReplaceContractRequest {
  targetContract: number
  replacementContract: number
}

export interface CreateRegistrationEntriesRequest {
  numberOfEntries: number
}

export interface UpdateRegistrationEntryRequest {
  id: number
  terminal?: string
  data: any
}

@Injectable()
export class ContractService {
  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  // GET /Domains/<code>/Registrations/<id>/Notes
  // POST /Domains/<code>/Registrations/<id>/Notes
  // DELETE /Domains/<code>/Registrations/<id>/Notes/<noteId>

  public postNote(domain: string, registrationId: string, note: Note) {
    const url =
      'api://Domains/' + domain + '/Registrations/' + registrationId + '/Notes'
    return this.http.post<Note>(url, note).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  public getNotes(domain: string, registrationId: string) {
    const url =
      'api://Domains/' + domain + '/Registrations/' + registrationId + '/Notes'
    return this.http.get<[Notes]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  public removeNote(domain: string, registrationId: string, noteId: string) {
    const url =
      'api://Domains/' +
      domain +
      '/Registrations/' +
      registrationId +
      '/Notes/' +
      noteId
    return this.http.delete<Note>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getContracts(
    request?: GetContractsRequest,
  ): Observable<GetContractsResponse> {
    if (!request) {
      return observableThrowError(new Error('get contracts request not set'))
    }

    const url = 'api://Domains/' + request.domain + '/Contracts'

    // convert all object properties to strings
    let params = new HttpParams()
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }
    if (request.exact) {
      params = params.set('search', '+domain:' + request.domain)
    }

    return this.http.get<GetContractsResponse>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  exportContracts(domain: string, contractIDs: number[]) {
    const contractString = contractIDs
      .map((id) => `contractIds=${id}`)
      .join('&')
    console.log('export? ', contractString)
    const url =
      'api://Domains/' + domain + '/Contracts/Export?' + contractString

    return this.http.get<any>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getContract(domain: string, id: number): Observable<Contract> {
    const url = 'api://Domains/' + domain + '/Contracts/' + id

    return this.http.get<Contract>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getContractEntries(
    request: GetContractEntriesRequest,
  ): Observable<GetContractEntriesResponse> {
    if (!request) {
      return observableThrowError(
        new Error('get contracts entries request not set'),
      )
    }

    const url =
      'api://Domains/' +
      request.domain +
      '/Contracts/' +
      request.id +
      '/Entries'
    // convert all object properties to strings
    let params = new HttpParams()
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }

    return this.http
      .get<GetContractEntriesResponse>(url, {params: params})
      .pipe(
        tap((res) => {
          this.logger.log(res)
        }),
      )
  }

  putContract(domain: string, contract: Contract): Observable<Contract> {
    const url = 'api://Domains/' + domain + '/Contracts/' + contract.id

    return this.http.put<Contract>(url, contract).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  postContract(domain: string, contract: Contract): Observable<Contract> {
    const url = 'api://Domains/' + domain + '/Contracts'

    return this.http.post<Contract>(url, contract).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  deleteContract(domain: string, id: number, force = false): Observable<void> {
    const url = 'api://Domains/' + domain + '/Contracts/' + id
    let options = {}
    if (force) {
      options = {params: new HttpParams().set('force', force.toString())}
    }
    return this.http.delete<void>(url, options).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public postAction(
    domain: string,
    id: number,
    action: ContractAction,
    immediate?: boolean,
  ): Observable<ContractAction> {
    const url = 'api://Domains/' + domain + '/Contracts/' + id + '/Actions'
    let params = new HttpParams()

    if (immediate) {
      params = params.set('immediate', String(immediate))
    }

    return this.http.post<ContractAction>(url, action, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  patchContractEntries(
    domain: string,
    id: number,
    request: UpdateContractEntryRequest[],
  ): Observable<ContractEntry[]> {
    const url = 'api://Domains/' + domain + '/Contracts/' + id + '/Entries'

    return this.http.patch<ContractEntry[]>(url, request).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  deleteContractEntry(
    domain: string,
    contract: number,
    id: number,
  ): Observable<void> {
    const url =
      'api://Domains/' + domain + '/Contracts/' + contract + '/Entries/' + id

    return this.http.delete<void>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getContractTypes(domain: string): Observable<ContractType[]> {
    const url = 'api://Domains/' + domain + '/Contracts/AvailableContractTypes'

    return this.http.get<ContractType[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getRegistrations(
    request: GetRegistrationsRequest,
  ): Observable<GetRegistrationsResponse> {
    const url = 'api://Domains/' + request.domain + '/Registrations'

    // convert all object properties to strings
    let params = new HttpParams()
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }
    if (request.entries) {
      params = params.set('entries', request.entries.toString())
    }

    return this.http.get<GetRegistrationsResponse>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getRegistration(domain: string, id: number): Observable<Registration> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id

    return this.http.get<Registration>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  getAvailableContracts(domain: string): Observable<Contract[]> {
    const url = 'api://Domains/' + domain + '/Registrations/AvailableContracts'
    return this.http.get<Contract[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  postRegistration(
    domain: string,
    request: CreateRegistrationRequest,
  ): Observable<Registration> {
    const url = 'api://Domains/' + domain + '/Registrations'

    return this.http.post<Registration>(url, request).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  putRegistration(
    domain: string,
    registration: Registration,
  ): Observable<Registration> {
    const url = 'api://Domains/' + domain + '/Registrations/' + registration.id

    return this.http.put<Registration>(url, registration).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  patchRegistrationContracts(
    domain: string,
    id: number,
    request: ReplaceContractRequest,
  ): Observable<Contract[]> {
    const url =
      'api://Domains/' + domain + '/Registrations/' + id + '/Contracts'

    return this.http.patch<Contract[]>(url, request).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  postRegistrationContract(
    domain: string,
    id: number,
    contract: Contract,
  ): Observable<Contract> {
    const url =
      'api://Domains/' + domain + '/Registrations/' + id + '/Contracts'

    return this.http.post<Contract>(url, contract).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  deleteRegistrationContract(
    domain: string,
    id: number,
    contract: Contract,
  ): Observable<void> {
    const url =
      'api://Domains/' +
      domain +
      '/Registrations/' +
      id +
      '/Contracts/' +
      contract.id

    return this.http.delete<void>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  deleteRegistration(domain: string, id: number): Observable<void> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id

    return this.http.delete<void>(url)
  }

  getRegistrationEntries(
    domain: string,
    id: number,
  ): Observable<RegistrationEntry[]> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id + '/Entries'

    return this.http.get<RegistrationEntry[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  postRegistrationEntries(
    domain: string,
    id: number,
    request: CreateRegistrationEntriesRequest,
  ): Observable<RegistrationEntry[]> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id + '/Entries'

    return this.http.post<RegistrationEntry[]>(url, request).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  patchRegistrationEntries(
    domain: string,
    id: number,
    request: UpdateRegistrationEntryRequest[],
  ): Observable<RegistrationEntry[]> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id + '/Entries'

    return this.http.patch<RegistrationEntry[]>(url, request).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  deleteRegistrationEntries(
    domain: string,
    id: number,
    entries: number[],
  ): Observable<RegistrationEntry[]> {
    const url = 'api://Domains/' + domain + '/Registrations/' + id + '/Entries'

    return this.http
      .request<RegistrationEntry[]>('DELETE', url, {body: entries})
      .pipe(
        tap((res) => {
          this.logger.log(res)
        }),
      )
  }
}
